import React, { useContext, useEffect } from 'react';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { PaginasOutrosRegimes } from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import styles from './receitaBruta.module.scss';
import ComponenteNCM from '../../../componenteNCM/componenteNCM';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import { IReceitaBrutaNCMReceber } from '../../../../../../../../../interfaces/IReceitaBruta';
import { useQuery } from '@tanstack/react-query';
import { ETipoOperacao } from '../../../../../../../../../enums/ETipoOperacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IReceitaBrutaProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  setBaseDeCalculo: React.Dispatch<
    // eslint-disable-next-line no-undef
    React.SetStateAction<PaginasOutrosRegimes>
  >;
}

const ReceitaBruta = ({ isEdicao, setIsEdicao, setBaseDeCalculo }: IReceitaBrutaProps) => {
  const [isEdicaoNcm, setIsEdicaoNcm] = React.useState<boolean>(false);
  const [isLoadingPost, setIsLoadingPost] = React.useState<boolean>(false);

  const { postReceitaBruta, deleteReceitaBruta, getListaNCMsAdicionadosReceitaBruta } = useBackEndRequests();
  const context = useContext(DiefCfemContext);

  const mostrarNotificacao = useNotificacao();

  const {
    data: listaNCMData,
    isLoading: isListaNCMLoading,
    error: listaNCMError,
    refetch: refetchListaNCM,
  } = useQuery<IReceitaBrutaNCMReceber[], IErrorWithSentryId>({
    queryKey: ['getListaNCMReceitaBruta', context?.idReceitaBruta],
    queryFn: () => getListaNCMsAdicionadosReceitaBruta(context?.idReceitaBruta ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: context?.idReceitaBruta !== -1,
  });

  const deleteReceita = async () => {
    await deleteReceitaBruta(context?.idReceitaBruta || -1)
      .then(() => {
        setIsEdicao(false);
        setBaseDeCalculo('lista');
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao cancelar Receita Bruta, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  useEffect(() => {
    if (!isEdicao) {
      setIsLoadingPost(true);
      const postReceita = async () => {
        await postReceitaBruta(context?.idOperacaoEditar || '-1')
          .then((response) => {
            context?.setIdReceitaBruta(response.id);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao iniciar Receita Bruta, tente novamente.',
              ...(err && { complementoSentryId: err }),
            });
            setBaseDeCalculo('lista');
          });
      };
      postReceita();
      setIsLoadingPost(false);
    }
  }, []);

  return (
    <div className={styles.container}>
      {listaNCMError ? (
        <ErrorMessage
          errorMessage={listaNCMError.message}
          error={listaNCMError}
          onClickFunction={() => {
            setIsEdicao(false);
            setBaseDeCalculo('lista');
          }}
        />
      ) : (
        <>
          {isLoadingPost ? (
            <div className={styles.loadingContainer}>
              <BrLoading large />
            </div>
          ) : (
            <>
              <div className={styles.titleContainer}>
                <span className={styles.title}>Operação - Receita Bruta de Vendas (-) Tributos Incidentes</span>
              </div>
              <div className={styles.ncmContainer}>
                <ComponenteNCM
                  isEdicaoNcm={isEdicaoNcm}
                  setIsEdicaoNcm={setIsEdicaoNcm}
                  ncmListaData={listaNCMData}
                  isListaNCMLoading={isListaNCMLoading}
                  listaNCMError={listaNCMError}
                  refetchListaNCM={refetchListaNCM}
                  type={ETipoOperacao.RECEITA_BRUTA_TRIBUTOS_INCIDENTES}
                  insideTitle
                />
              </div>
              <div className={styles.containerButton}>
                {!isEdicao && (
                  <BrButton secondary onClick={() => deleteReceita()}>
                    <span className={styles.button}>
                      <i className={`fas fa-trash`} /> Cancelar
                    </span>
                  </BrButton>
                )}
                <BrButton
                  onClick={() =>
                    listaNCMData?.length === 0
                      ? mostrarNotificacao({
                          tipo: 'danger',
                          conteudo: 'Insira pelo menos um NCM para continuar.',
                        })
                      : setBaseDeCalculo('lista')
                  }
                  primary
                >
                  <span className={styles.button}>
                    {isEdicao ? (
                      <>
                        <i className={`fas fa-save`} /> Salvar Base de Cálculo
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                      </>
                    )}
                  </span>
                </BrButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReceitaBruta;
